import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {
    getLayoutCountsFailure,
    getLayoutCountsRequest,
    getLayoutCountsSuccess,
} from 'src/openapi/admin/apis/UserLayoutsApi';

type LayoutsCountState = {
    total: number,
    status: 'idle' | 'loading' | 'succeeded' | 'failed',
    error?: string,
    receivedAt?: number,
};

export type LayoutsCountPageState = {
    userPage: {
        tabs: {
            content: {
                layoutsCount: LayoutsCountSliceState,
            },
        },
    },
};

type LayoutsCountSliceState = {
    countsByUserId: Record<number, LayoutsCountState>,
};

const initialState: LayoutsCountSliceState = {
    countsByUserId: {},
};

const computeTotal = (data: Record<string, number | null | undefined>): number => {
    if (!data) {
        return 0;
    }

    const values = Object.values(data) as Array<number | null | undefined>;

    return values.reduce((sum: number, val) => sum + (val ?? 0), 0);
};

export const layoutsCountSlice = createSlice({
    name: 'layoutsCount',
    initialState,
    reducers: {
        invalidateLayoutsCount: (state, action: PayloadAction<number>) => {
            const userId = action.payload;
            const userLayouts = state.countsByUserId[userId];
            if (userLayouts) {
                userLayouts.status = 'idle';
                userLayouts.receivedAt = undefined;
            }
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getLayoutCountsRequest, (state, action) => {
                const {userId} = action.payload.meta;
                if (!state.countsByUserId[userId]) {
                    state.countsByUserId[userId] = {
                        total: 0,
                        status: 'loading',
                    };
                } else {
                    state.countsByUserId[userId].status = 'loading';
                }
            })
            .addCase(getLayoutCountsSuccess, (state, action) => {
                const {userId} = action.payload.meta;
                const data = action.payload.data;
                const total = computeTotal(data);

                state.countsByUserId[userId] = {
                    total,
                    status: 'succeeded',
                    receivedAt: Date.now(),
                };
            })
            .addCase(getLayoutCountsFailure, (state, action) => {
                const {userId} = action.payload.meta;
                state.countsByUserId[userId] = {
                    total: 0,
                    status: 'failed',
                    error: action.payload.data?.message ?? 'Unknown error',
                };
            });
    },
});

export const {invalidateLayoutsCount} = layoutsCountSlice.actions;
